import fuzzysort from 'fuzzysort';
import { useEffect, useMemo, useState } from 'react';
import type { ValueOption } from '../../types/widget.js';
import { useFloatingList } from './useFloatingList.js';

interface UseComboboxProps {
  options: ValueOption[];
  onSelect: (option?: ValueOption) => void;
  initialValue?: string;
}

export const useCombobox = ({ options, onSelect, initialValue = '' }: UseComboboxProps) => {
  const [searchText, setSearchText] = useState(initialValue);
  const [open, setOpen] = useState<boolean>(false);
  const [active, setActive] = useState<number | null>(null);
  const [selectedOption, setSelectedOption] = useState<ValueOption | undefined>(
    options.find((option) => option.label === initialValue),
  );

  useEffect(() => {
    if (initialValue) {
      setSearchText(initialValue);
    }
  }, [initialValue]);

  const filteredOptions = useMemo(() => {
    if (searchText.trim().length === 0) {
      return options.map((option) => ({
        obj: option,
        label: option.label,
      })) as unknown as Fuzzysort.KeyResults<ValueOption>;
    }
    return fuzzysort.go(searchText, options, {
      key: 'label',
      limit: 10,
    });
  }, [searchText, options]);

  const highlightFuzzyResult = (result: Fuzzysort.KeyResult<ValueOption>) => {
    if (!result) return null;
    const s = '\x01';
    const highlighted = result.highlight(s, s);
    if (!highlighted) return result.target;
    return highlighted.split(s).map((part, i) =>
      i % 2 === 1 ? (
        <span style={{ fontWeight: 'bold' }} key={part}>
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const getMaxHeight = () => '400px';

  const {
    x,
    y,
    strategy,
    refs,
    context,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    activeIndex,
    setActiveIndex,
    listRef,
  } = useFloatingList(open, setOpen, false, getMaxHeight, 'bottom-start', false);

  const handleSelect = (option: ValueOption) => {
    setSearchText(option.label);
    setSelectedOption(option);
    onSelect(option);
    setOpen(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchText(e.target.value);
    setOpen(true);

    setActiveIndex(0); // Always set the first item active while typing to able to select it with the Enter key

    if (selectedOption && selectedOption.label !== inputValue) {
      setSelectedOption(undefined);
      onSelect(undefined);
    }
  };

  const clearText = () => {
    setSearchText('');
    setSelectedOption(undefined);
    onSelect(undefined);
  };

  return {
    searchText,
    setSearchText,
    active,
    setActive,
    open,
    setOpen,
    filteredOptions,
    highlightFuzzyResult,
    x,
    y,
    strategy,
    refs,
    context,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    activeIndex,
    listRef,
    handleSelect,
    handleChange,
    clearText,
  };
};
